import { Fragment } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  CreateButton,
  Loading,
  TopToolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type { AdminDataProvider, MenuItem } from '../dataProvider';
import { childrenOf, describeLink, locationsOf, topLevel } from './menuModel';

/**
 * The navigation menu surface (D-cms-menu, the LAST CMS piece). Items render as a 2-LEVEL
 * TREE per LOCATION (a free key, e.g. 'header' / 'footer'): top-level items are one sortable
 * list, each parent's children are their OWN sortable list nested under it. The within-level
 * reorder reuses the composer / slider dnd-kit pattern (one DndContext per level, so a drag
 * NEVER crosses levels or locations); re-parenting is an EXPLICIT control on the form (a
 * parent select), NOT a free nested-drag tree. Reorder rewrites the level's `order` integers
 * through the chokepoint (cms:author), audited. OPERATIONAL market-scoped; an 'all'-market
 * item is ONE shared row visible in both scopes (marked 'all'). NO publish: the active toggle
 * is the whole lifecycle (like sliders).
 */

const marketLabel = (m: string) => (m === 'all' ? 'ALL' : m.toUpperCase());

const SortableMenuItemCard = ({
  item,
  index,
  total,
  canAuthor,
  onMove,
  onToggle,
  onEdit,
}: {
  item: MenuItem;
  index: number;
  total: number;
  canAuthor: boolean;
  onMove: (dir: -1 | 1) => void;
  onToggle: () => void;
  onEdit: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ mb: 1 }}
      data-testid={`menu-card-${item.id}`}
    >
      <CardContent sx={{ py: 1 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', px: 0.5, color: 'text.secondary' }}
            data-testid={`menu-drag-${item.id}`}
            aria-label="Drag to reorder"
          >
            ::
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`menu-link-${item.id}`}
            >
              {describeLink(item.link)}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={marketLabel(item.market)}
            color={item.market === 'all' ? 'info' : 'default'}
            variant="outlined"
            data-testid={`menu-market-${item.id}`}
          />
          <Chip
            size="small"
            label={item.active ? 'Active' : 'Inactive'}
            color={item.active ? 'success' : 'default'}
            variant={item.active ? 'filled' : 'outlined'}
            data-testid={`menu-active-${item.id}`}
          />
          {canAuthor && (
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                disabled={index === 0}
                onClick={() => onMove(-1)}
                data-testid={`menu-up-${item.id}`}
              >
                Up
              </Button>
              <Button
                size="small"
                disabled={index === total - 1}
                onClick={() => onMove(1)}
                data-testid={`menu-down-${item.id}`}
              >
                Down
              </Button>
              <Button
                size="small"
                onClick={onToggle}
                data-testid={`menu-toggle-${item.id}`}
              >
                {item.active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                size="small"
                onClick={onEdit}
                data-testid={`menu-edit-${item.id}`}
              >
                Edit
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const SortableLevel = ({
  items,
  canAuthor,
  onReorder,
  onToggle,
  onEdit,
  renderChildren,
}: {
  items: MenuItem[];
  canAuthor: boolean;
  onReorder: (siblings: MenuItem[], ordered: MenuItem[]) => void;
  onToggle: (item: MenuItem) => void;
  onEdit: (item: MenuItem) => void;
  renderChildren?: (item: MenuItem) => React.ReactNode;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= items.length) return;
    onReorder(items, arrayMove(items, index, to));
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from === -1 || to === -1) return;
    onReorder(items, arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item, index) => (
          <Fragment key={item.id}>
            <SortableMenuItemCard
              item={item}
              index={index}
              total={items.length}
              canAuthor={canAuthor}
              onMove={(dir) => move(index, dir)}
              onToggle={() => onToggle(item)}
              onEdit={() => onEdit(item)}
            />
            {renderChildren?.(item)}
          </Fragment>
        ))}
      </SortableContext>
    </DndContext>
  );
};

const MenuManagerActions = ({ canAuthor }: { canAuthor: boolean }) => (
  <TopToolbar>
    {canAuthor && <CreateButton resource="menuItems" label="New item" />}
  </TopToolbar>
);

/** The manager itself. Exported for testing without the access wrapper. */
export const MenuManagerView = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );

  // The active markets PLUS the all-market items (a both item shows in every scope).
  const inScope = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  const { data, isLoading } = useGetList<MenuItem>('menuItems', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'order', order: 'ASC' },
    filter: { market: inScope },
  });

  if (isLoading) return <Loading />;

  const items = data ?? [];
  const locations = locationsOf(items);

  // Persist a level's new sequence: reassign the SAME set of the level's order integers to
  // the new arrangement, writing back only the changed items through the chokepoint.
  const onReorder = async (siblings: MenuItem[], ordered: MenuItem[]) => {
    const orderValues = siblings.map((s) => s.order);
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const item = ordered[i];
        const nextOrder = orderValues[i];
        if (item.order !== nextOrder) {
          await performStatusTransition({
            dataProvider,
            role,
            capability: 'cms:author',
            resource: 'menuItems',
            id: item.id,
            data: { order: nextOrder, updatedAt: new Date().toISOString() },
            previousData: item,
          });
        }
      }
      refresh();
    } catch {
      notify('Could not reorder the menu', { type: 'error' });
    }
  };

  const onToggle = async (item: MenuItem) => {
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'cms:author',
        resource: 'menuItems',
        id: item.id,
        data: { active: !item.active, updatedAt: new Date().toISOString() },
        previousData: item,
      });
      notify(item.active ? 'Item deactivated' : 'Item activated', {
        type: 'success',
      });
      refresh();
    } catch {
      notify('Could not change the item', { type: 'error' });
    }
  };

  const onEdit = (item: MenuItem) => redirect('edit', 'menuItems', item.id);

  return (
    <Box data-testid="menu-manager">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" component="h1">
          Navigation menu
        </Typography>
        <MenuManagerActions canAuthor={canAuthor} />
      </Stack>
      {locations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No menu items in this region yet.
        </Typography>
      ) : (
        locations.map((loc) => (
          <Box key={loc} sx={{ mb: 3 }} data-testid={`location-${loc}`}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {loc}
            </Typography>
            <SortableLevel
              items={topLevel(items, loc)}
              canAuthor={canAuthor}
              onReorder={onReorder}
              onToggle={onToggle}
              onEdit={onEdit}
              renderChildren={(parent) => {
                const kids = childrenOf(items, parent.id);
                if (kids.length === 0) return null;
                return (
                  <Box
                    sx={{ pl: 4, mb: 1 }}
                    data-testid={`menu-children-${parent.id}`}
                  >
                    <SortableLevel
                      items={kids}
                      canAuthor={canAuthor}
                      onReorder={onReorder}
                      onToggle={onToggle}
                      onEdit={onEdit}
                    />
                  </Box>
                );
              }}
            />
          </Box>
        ))
      )}
    </Box>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const MenuManager = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <MenuManagerView />
  </RequireAccess>
);
